import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import {
  ALL_DENIED, ALL_GRANTED, CONSENT_COOKIE_NAME, CONSENT_MAX_AGE_DAYS, CONSENT_SCHEMA_VERSION,
  type ConsentCategories, type ConsentMethod, type ConsentRecord, type ConsentRegime,
} from '@shared';
import { applyConsent, ensureGtagLoaded } from './consent-mode';
import { useLegal } from './legal-context';

// Single source of truth for cookie consent. Mirrors the AuthProvider /
// LegalProvider pattern — plain React context, no extra dependency. Reads
// hydrate from the first-party cookie (falling back to localStorage); writes
// fan out to cookie + localStorage + Google Consent Mode + a best-effort
// server audit. Slots between LegalProvider (needs cmsLegal.version) and the
// app tree in App.tsx.

const MAX_AGE_SECONDS = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;

// EU/EEA + UK — the opt-in (GDPR/ePrivacy) bloc. UAE (PDPL) and Singapore
// (PDPA) are mapped to the same opt-in treatment in regimeFor() below.
const EU_EEA_UK = new Set([
  'GB', 'IE', 'FR', 'DE', 'ES', 'IT', 'NL', 'SE', 'PL', 'PT', 'DK', 'FI', 'BE',
  'AT', 'CZ', 'GR', 'HU', 'RO', 'BG', 'HR', 'CY', 'EE', 'LV', 'LT', 'LU', 'MT',
  'SK', 'SI', 'IS', 'LI', 'NO',
]);

function regimeFor(region: string): ConsentRegime {
  const cc = (region.split('-')[0] ?? region).toUpperCase();
  if (EU_EEA_UK.has(cc)) return 'gdpr';
  if (cc === 'US') return 'ccpa';
  if (cc === 'AE') return 'pdpl';
  if (cc === 'SG') return 'pdpa';
  return 'unknown';
}

function readStored(): ConsentRecord | null {
  const cookieVal = document.cookie.match(/(?:^|;\s*)omega_consent=([^;]+)/)?.[1];
  const raw = cookieVal != null
    ? decodeURIComponent(cookieVal)
    : (() => { try { return localStorage.getItem(CONSENT_COOKIE_NAME); } catch { return null; } })();
  if (!raw) return null;
  try { return JSON.parse(raw) as ConsentRecord; } catch { return null; }
}

function writeStored(record: ConsentRecord): void {
  const value = encodeURIComponent(JSON.stringify(record));
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  try { localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(record)); } catch { /* private mode — cookie still set */ }
}

interface ConsentContextValue {
  record: ConsentRecord | null;
  regime: ConsentRegime;
  /** Show the first-visit banner: hydration done AND no valid record. */
  needsBanner: boolean;
  prefsOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (categories: ConsentCategories, method: ConsentMethod) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
  return ctx;
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const legal = useLegal();
  const policyVersion = legal.version ?? '0';

  const [record, setRecord] = useState<ConsentRecord | null>(null);
  const [region, setRegion] = useState('unknown');
  const [hydrated, setHydrated] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Region: ask the server (it can read an edge geo header), with a timezone
  // hint as an offline fallback. Never blocks the UI — defaults to 'unknown'
  // (= strictest opt-in) if detection fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let detected = 'unknown';
      try {
        const res = await fetch('/api/consent/region', { credentials: 'include' });
        if (res.ok) detected = (await res.json())?.region ?? 'unknown';
      } catch { /* fall through to timezone */ }
      if (detected === 'unknown') {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
        if (tz === 'Asia/Dubai') detected = 'AE';
        else if (tz === 'Asia/Singapore') detected = 'SG';
        else if (tz.startsWith('Europe/')) detected = 'GB';
        else if (tz.startsWith('America/')) detected = 'US';
      }
      if (!cancelled) setRegion(detected);
    })();
    return () => { cancelled = true; };
  }, []);

  // Hydrate any existing consent. A record is only valid if it matches the
  // current schema AND the current policy version — a policy bump (material
  // change) invalidates it and re-prompts, mirroring REQ-012.
  useEffect(() => {
    const existing = readStored();
    const valid =
      existing != null &&
      existing.v === CONSENT_SCHEMA_VERSION &&
      existing.policy_version === policyVersion;
    if (valid) {
      setRecord(existing);
      ensureGtagLoaded();
      applyConsent(existing!);
    } else {
      setRecord(null);
    }
    setHydrated(true);
  }, [policyVersion]);

  const regime = regimeFor(region);

  const persist = useCallback(
    (categories: ConsentCategories, method: ConsentMethod, gpc = false) => {
      const next: ConsentRecord = {
        v: CONSENT_SCHEMA_VERSION,
        policy_version: policyVersion,
        method,
        regime: regimeFor(region),
        region,
        categories: { ...categories, necessary: true },
        gpc,
        updated_at: new Date().toISOString(),
      };
      writeStored(next);
      setRecord(next);
      // Newly-granted categories can fire immediately; newly-denied are revoked.
      ensureGtagLoaded();
      applyConsent(next);
      // Best-effort server audit (REQ-007) — only persisted for signed-in
      // users (see routes/consent.ts). Never blocks or breaks the UX.
      fetch('/api/consent', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(next),
      }).catch(() => { /* ignore — client record is the source of truth */ });
    },
    [policyVersion, region],
  );

  // CCPA + Global Privacy Control → automatic opt-out, no banner shown. GPC is
  // a legally-recognised opt-out signal under CPRA; honouring it is mandatory.
  useEffect(() => {
    if (!hydrated || record) return;
    const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
    if (gpc && regimeFor(region) === 'ccpa') {
      persist(ALL_DENIED, 'gpc_auto', true);
    }
  }, [hydrated, record, region, persist]);

  const value = useMemo<ConsentContextValue>(() => ({
    record,
    regime,
    needsBanner: hydrated && record === null,
    prefsOpen,
    openPreferences: () => setPrefsOpen(true),
    closePreferences: () => setPrefsOpen(false),
    // Close the Preference Center too, so these work identically whether
    // clicked from the banner or from inside the modal.
    acceptAll: () => { persist(ALL_GRANTED, 'accept_all'); setPrefsOpen(false); },
    rejectAll: () => { persist(ALL_DENIED, 'reject_all'); setPrefsOpen(false); },
    save: (categories, method) => { persist(categories, method); setPrefsOpen(false); },
  }), [record, regime, hydrated, prefsOpen, persist]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
